import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// Box migrated to UDS
import moment from 'moment';


import Download from '@telus-uds/palette-allium/build/web/icons/Download';
import { Button, Spinner, Typography, Checkbox, Select, Divider, Box, TextArea } from '@telus-uds/components-web';
import EN from '../../../EN.json';
import FR from '../../../FR.json';
import {
  SummaryWrapper,
  DefectAttachmentRow,
  DefectSummaryFirstCol,
  DefectSummaryRow,
  DefectSummarySecondCol,
  OrderDetailsBox
} from '../css';
import DefectSummaryHeader from './DefectSummaryHeader';
import { useLocaleContext } from '../../context/LocaleContext';
import { GET_DEFECT_LOCALE, GET_WORKING_DAYS } from '../constants';

import request from '../../utils/request';
import DefectAssignee from './DefectAssignee';
import DefectComments from './DefectComments';

const DefectSummary = ({
  setFetchData,
  openModal,
  defect,
  userProfile,
  defectId
}) => {
  const [count, setCount] = useState(0);
  const locale = useLocaleContext();
  const [defectData, setDefectData] = useState(defect);
  const [hasError, setHasError] = useState(false);
  const [errorTitle, setErrorTitle] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const {
    DEFECT_MANAGEMENT: {
      DEFECT_SUMMARY = '',
      ATTACHMENTS = '',
      SUMMARY_TABLE = {},
      COMMENTS = '',
      ADD_COMMENT = '',
      ADD_BUTTON = '',
      FILTER_BY_STATUS = {},
      STATUS_OPTIONS,
      DEFAULT_STATUS_OPTIONS,
      STATUS = '',
      NO_ATTACHMENTS = '',
      CHANGE_STATUS = '',
      FOR_ADMINS_ONLY = ''
    } = {},
    general: { MAX_1000_CHARS }
  } = locale === 'en-US' ? EN : FR;
  const {
    defectWebform = '',
    createdAt = '',
    priority = '',
    id = '',
    reportIssueAttachments = '',
    status: defectStatus,
    comments = '',
    new_update_for_admin = '',
    new_update_for_reporter = ''
  } = defectData || {};

  const comment = React.createRef();
  const profile = request.getProfile();
  const { roleId, id: userId } = profile;
  const isAdmin = roleId === 1;

  const [assignee, setAssignee] = useState([]);
  const [selectedAssignee, setSelectedAssignee] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [isAdminOnly, setIsAdminOnly] = useState(false);
  const [assigneeModal, setAssigneeModal] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);

  const updatedDefects = comments
    ? { ...defectData }
    : { ...defectData, comments: 'No comments' };

  // states
  const [updatedComment, setUpdatedComment] = useState('');
  const [status, setStatus] = useState('');
  const [commentsList, setCommentsList] = useState(
    defectData.defectComments || []
  );

  let moved_to_in_progress_at = '';
  let resolved_at = '';

  useEffect(() => {
    const assigneeObject = defectData.defectAssignedTo
      ? {
        id: defectData.assigned_user_id,
        label:
          defectData.defectAssignedTo &&
          (defectData.defectAssignedTo.firstName ||
            defectData.defectAssignedTo.lastName) &&
          `${defectData.defectAssignedTo.firstName} ${defectData.defectAssignedTo.lastName}`
      }
      : { id: 0, label: '' };
    setAssignee([assigneeObject] || []);
    setSelectedAssignee([assigneeObject]);
    if (defectData.moved_to_in_progress_at) {
      setCount(GET_WORKING_DAYS(defectData.moved_to_in_progress_at));
    }
  }, [defectData]);

  const updatedDefectComment = () => {
    if (updatedComment && updatedComment.length > 0) {
      const body = {
        text: updatedComment,
        is_admin_only: isAdminOnly,
        defect_id: id,
        user_id: profile && profile.email,
        commenter_name: profile && profile.name,
        commenter_role_id: roleId
      };
      setSubmitting(true);
      request
        .post(`/defects/createcomments/${id}`, body)
        .then(res => {
          const { data = {} } = res;
          if (data.success === 1) {
            openModal(false);
            setFetchData(true);
          }
        })
        .catch(err => new Error(err))
        .finally(() => {
          setSubmitting(false);
        });
    }
  };

  const getCurrentDefect = () => {
    // The backend takes the user and role from the session; the path values are only placeholders.
    request.get(`/defects/getonedefect/${id || defectId}/${userId}/${roleId}`).then(response => {
      if (response && response.status === 200) {
        if (response.data.innerData) {
          setHasError(false);
          setDefectData(response.data.innerData);
          setCommentsList(response.data.innerData.defectComments || []);
          updateDefectAdminNotification();
        } else {
          setHasError(true);
          setErrorTitle('');
          setErrorMessage('No Record Found');
        }
      } else {
        setHasError(true);
        setErrorTitle('Access Denied!');
        setErrorMessage(`You don't have permission to view this Defect`);
      }
    }).catch(() => {
      // 404: defect does not exist or belongs to another user
      setHasError(true);
      setErrorTitle('Access Denied!');
      setErrorMessage(`You don't have permission to view this Defect`);
    });
  };

  const updateDefectAdminNotification = () => {
    let url = '';
    let body = {};
    try {
      if (id) {
        if (roleId === 1) {
          if (new_update_for_admin === true) {
            url = `/defects/admin-notification/${id}`;
            body = {
              new_update_for_admin: false
            };
          }
        } else {
          if (new_update_for_reporter === true) {
            url = `/defects/reporter-notification/${id}`;
            body = {
              new_update_for_reporter: false
            };
          }
        }
        if (url) {
          request.put(url, body).catch(err => new Error(err));
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    getCurrentDefect();
    // The admin list only feeds the assignee modal (admins only).
    if (isAdmin) getDefectAdmins();
  }, [defectId]);

  useEffect(() => {
    if (defectData.moved_to_in_progress_at) {
      setCount(GET_WORKING_DAYS(defectData.moved_to_in_progress_at));
    }
  }, []);

  const getDefectAdmins = () => {
    request.get('/defects/getadmins').then(response => {
      if (response && response.data) {
        let adminUsers = [];
        response.data.adminList &&
          response.data.adminList.length > 0 &&
          response.data.adminList.map(({ id, firstName, lastName, email }) => {
            adminUsers.push({
              id,
              label: `${firstName} ${lastName}`,
              email
            });
          });
        setAdminUsers(adminUsers || []);
      }
    });
  };

  const updatedDefectStatus = () => {
    if (isAdmin && updatedComment) {
      updatedDefectComment();
    }
    if (assignee && assignee.length > 0 && assignee[0].label) {
      if (status) {
        if (status === STATUS_OPTIONS[1]) {
          // In Progress
          moved_to_in_progress_at = moment.utc().format();
        }
        if (status === STATUS_OPTIONS[2] || status === STATUS_OPTIONS[3]) {
          // Resolved || Closed - No Response
          resolved_at = moment.utc().format();
        }
        const body = {
          status,
          moved_to_in_progress_at,
          resolved_at,
          // new_update_for_admin: true,
          new_update_for_reporter: true
        };
        setSubmitting(true);
        request
          .post(`/defects/updatestatus/${id}`, body)
          .then(res => {
            const { data = {} } = res;
            if (data.success === 1) {
              openModal(false);
              setFetchData(true);
            }
          })
          .catch(err => new Error(err))
          .finally(() => {
            setSubmitting(false);
          });
      }
    }
  };

  const onAssigneeChange = selectedValue => {
    setSelectedAssignee(selectedValue || []);
  };

  const onClickSave = () => {
    if (selectedAssignee[0]) {
      const body = {
        assigned_user_id: selectedAssignee[0] && selectedAssignee[0].email,
        new_update_for_admin: true
        //new_update_for_reporter: true,
      };
      request
        .put(`/defects/update-assignee/${defectData.id}`, body)
        .then(res => {
          setAssignee(selectedAssignee);
        })
        .catch(err => new Error(err));
    }
  };

  const getUpdatedList = () => {
    if (count >= 5) return STATUS_OPTIONS;
    else return DEFAULT_STATUS_OPTIONS;
  };

  return (
    <>
      {!hasError ? (
        <SummaryWrapper>
          <Box bottom={4}>
            <OrderDetailsBox style={{ marginTop: '1.5rem' }}>
              <DefectSummaryHeader
                priority={GET_DEFECT_LOCALE(priority)}
                status={GET_DEFECT_LOCALE(defectStatus)}
                reportedOn={
                  moment
                    .utc(createdAt)
                    .format('dddd, MMMM Do YYYY, h:mm:ss a') + ' UTC'
                }
                webForm={GET_DEFECT_LOCALE(defectWebform)}
                assignee={assignee}
                isAdmin={isAdmin}
                setAssigneeModal={setAssigneeModal}
              />
            </OrderDetailsBox>
            <Divider space={4} variant={{ inverse: true }}/>
            <Box bottom={3}>
              <Typography heading="h2" variant={{ size: 'h2' }} block>
                {DEFECT_SUMMARY}
              </Typography>
              {updatedDefects &&
                SUMMARY_TABLE &&
                Object.keys(SUMMARY_TABLE).map(key => {
                  let selectedLabel = SUMMARY_TABLE[key];
                  return (
                    <DefectSummaryRow>
                      <div className="row">
                        <div
                          className="col-4"
                          style={{ backgroundColor: '#f7f7f8' }}
                        >
                          <DefectSummaryFirstCol>
                            {selectedLabel}
                          </DefectSummaryFirstCol>
                        </div>
                        <div className="col-8">
                          <DefectSummarySecondCol>
                            {key === 'createdAt'
                              ? moment
                                  .utc(createdAt)
                                  .format('dddd, MMMM Do YYYY, h:mm:ss a') +
                                ' UTC'
                              : GET_DEFECT_LOCALE(
                                  updatedDefects[key]
                                    ? updatedDefects[key]
                                    : '-'
                                )}
                          </DefectSummarySecondCol>
                        </div>
                      </div>
                    </DefectSummaryRow>
                  );
                })}
            </Box>
            <Divider space={3} variant={{ inverse: true }}/>
            <Box bottom={4}>
              <Typography heading="h2" variant={{ size: 'h2' }} block>
                {ATTACHMENTS}
              </Typography>
              <Divider space={1} variant={{ inverse: true }}/>
              <DefectAttachmentRow>
                <div className="row">
                  <div className="col-8">
                    {reportIssueAttachments || NO_ATTACHMENTS}
                  </div>
                  {reportIssueAttachments && (
                    <div style={{ textAlign: 'end' }} className="col-4">
                      <Button
                        icon={Download}
                        onPress={() => window.open(reportIssueAttachments, '_blank')}
                        accessibilityLabel="Download attachment"
                        variant={{ text: true }}
                      >
                        Download
                      </Button>
                    </div>
                  )}
                </div>
              </DefectAttachmentRow>
              <Divider space={3} variant={{ inverse: true }}/>
            </Box>
            <Box bottom={4}>
              <Typography heading="h2" variant={{ size: 'h2' }} block>
                {COMMENTS}
              </Typography>
              {commentsList && commentsList.length > 0 && (
                <DefectComments data={commentsList} isAdmin={isAdmin} />
              )}
              <Divider space={1} variant={{ inverse: true }}/>
              <div
                style={{
                  textAlign: 'center',
                  marginTop: '10px',
                  marginBottom: '10px'
                }}
              >
                <div>
                  <TextArea
                    label={ADD_COMMENT}
                    ref={comment}
                    maxLength={1000}
                    onChange={(value) => {
                      setUpdatedComment(value);
                    }}
                  />
                  <div
                    style={{
                      fontSize: '14px',
                      fontWeight: '900px',
                      color: '#ABABAB',
                      display: 'flex',
                      flexDirection: 'row'
                    }}
                  >
                    <Typography>{MAX_1000_CHARS}</Typography>
                  </div>
                </div>
                {isAdmin && (
                  <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                    <Checkbox
                      checked={isAdminOnly}
                      name="AdminOnly"
                      value={isAdminOnly}
                      onChange={(checked) => {
                        setIsAdminOnly(checked);
                      }}
                      label={FOR_ADMINS_ONLY}
                    />
                  </div>
                )}
                {!isAdmin && (
                  <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                    <Spinner
                      show={isSubmitting}
                      variant={{ size: 'small' }}
                      label="Request is processing."
                      inline
                    >
                      <Button
                        key={1}
                        variant={{ priority: 'low' }}
                        onClick={updatedDefectComment}
                      >
                        {ADD_BUTTON}
                      </Button>
                    </Spinner>
                  </div>
                )}
              </div>
            </Box>
            {assigneeModal && adminUsers.length > 0 && (
              <DefectAssignee
                assignee={assignee}
                selectedAssignee={selectedAssignee}
                adminUsers={adminUsers}
                onAssigneeChange={onAssigneeChange}
                onClickSave={onClickSave}
                setAssigneeModal={setAssigneeModal}
              />
            )}
            {userProfile.role.name === 'ADMIN' ? (
              <Box bottom={4}>
                <Divider space={2} variant={{ inverse: true }}/>
                <Select
                  label={CHANGE_STATUS}
                  placeholder={FILTER_BY_STATUS.placeholder}
                  inactive={
                    !(assignee && assignee.length > 0 && assignee[0].label)
                  }
                  value={status}
                  onChange={(value) => {
                    setStatus(value);
                  }}
                >
                  {getUpdatedList().map(o => (
                    <Select.Item key={o} value={o}>
                      {o}
                    </Select.Item>
                  ))}
                </Select>
                <div
                  style={{
                    textAlign: 'center',
                    marginTop: '10px',
                    marginBottom: '10px'
                  }}
                >
                  <Spinner
                    show={isSubmitting}
                    variant={{ size: 'small' }}
                    label="Request is processing."
                    inline
                  >
                    <Button
                      key={2}
                      variant={{ priority: 'low' }}
                      onClick={updatedDefectStatus}
                    >
                      {ADD_BUTTON}
                    </Button>
                  </Spinner>
                </div>
                <Divider space={2} variant={{ inverse: true }}/>
              </Box>
            ) : (
              ''
            )}
          </Box>
        </SummaryWrapper>
      ) : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            height: '500px'
          }}
        >
          <Typography>{errorTitle}</Typography>
          <Typography>{errorMessage}</Typography>
        </div>
      )}
    </>
  );
};

DefectSummary.propTypes = {
  defect: PropTypes.objectOf(PropTypes.any).isRequired,
  openModal: PropTypes.func.isRequired,
  setFetchData: PropTypes.func.isRequired
};

export default DefectSummary;
